import { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';

const AuthErrorHandler = () => {
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [isClearing, setIsClearing] = useState<boolean>(false);
  const { clearAuthCache, login } = useAuth();

  useEffect(() => {
    const handleAuthError = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (detail && detail.status === 401) {
        setShowDialog(true);
      }
    };

    window.addEventListener('auth-error', handleAuthError);

    return () => {
      window.removeEventListener('auth-error', handleAuthError);
    };
  }, []);

  const handleClearCacheAndReauth = async () => {
    setIsClearing(true);
    try {
      clearAuthCache();
      
      await new Promise<void>(resolve => setTimeout(resolve, 500));
      
      await login();
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error during cache clear and re-auth:', error);
      }
    } finally {
      setIsClearing(false);
      setShowDialog(false);
    }
  };

  const handleDismiss = () => {
    setShowDialog(false);
  };

  if (!showDialog) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl">
        <div className="flex items-center mb-4">
          <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center mr-3">
            <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Authentication Issue</h3>
        </div>
        
        <p className="text-gray-600 mb-6">
          We're having trouble accessing the backend. This sometimes happens after backend deployments or JWT changes. 
          Clearing your authentication cache and re-authenticating usually fixes this issue.
        </p>
        
        <div className="flex space-x-3">
          <button
            onClick={handleClearCacheAndReauth}
            disabled={isClearing}
            className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {isClearing ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Clearing...
              </>
            ) : (
              'Clear Cache & Re-authenticate'
            )}
          </button>
          
          <button
            onClick={handleDismiss}
            disabled={isClearing}
            className="px-4 py-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Dismiss
          </button>
        </div>
        
        <p className="text-xs text-gray-500 mt-3">
          This will clear your local authentication tokens and require you to sign in again.
        </p>
      </div>
    </div>
  );
};

export default AuthErrorHandler;
